/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable complexity */
/* eslint-disable react/forbid-foreign-prop-types */
import type React from 'react';
import type { ComponentDocumentation, Hook, ReactDescT, TypescriptDocumentation } from './types.js';

const arrayFormat = (array: ReactDescT[]) => array.map((propType) => propTypeFormat(propType));

const shapeFormat = (shape: Record<string, ReactDescT>) => {
  const props = Object.keys(shape).map((key) => {
    const value = shape[key];
    let valueFormat;
    if (
      value.type &&
      (value.type === 'arrayOf' || value.type === 'oneOfType' || value.type === 'oneOf') &&
      Array.isArray(value.args)
    ) {
      valueFormat = `${propTypeFormat(value)}`;
    } else if (value.type === 'shape') {
      valueFormat = `${propTypeFormat(value)}`;
    } else {
      valueFormat = propTypeFormat(value);
    }
    return `${key}${value.reactDesc && value.reactDesc.required ? '' : '?'}: ${valueFormat}`;
  });
  return `{${props.join(',')}}`;
};

const propTypeFormat = (propType: ReactDescT | ReactDescT[], joinWith = ''): string => {
  let result;
  if (Array.isArray(propType)) {
    result = arrayFormat(propType).join(joinWith);
  } else if (typeof propType !== 'function' && propType.type) {
    switch (propType.type) {
      case 'array':
        result = 'any[]';
        break;
      case 'arrayOf':
        if ((propType.args as ReactDescT).type === 'oneOfType') {
          result = `(${propTypeFormat(propType.args as ReactDescT, ' | ')})[]`;
        } else {
          result = `${propTypeFormat(propType.args as ReactDescT, '\n')}[]`;
        }
        break;
      case 'tuple':
        result = `[${propTypeFormat(propType.args as ReactDescT, ', ')}]`;
        break;
      case 'bool':
        result = 'boolean';
        break;
      case 'func':
        result = propType?.reactDesc?.signature ?? '((...args: any[]) => any)';
        break;
      case 'node':
        result = 'React.ReactNode';
        break;
      case 'element':
        result = 'JSX.Element';
        break;
      case 'instanceOf':
        result = 'any';
        break;
      case 'symbol':
        result = 'any';
        break;
      case 'objectOf':
        result = `{ [key: string]: ${propTypeFormat(propType.args as ReactDescT)} }`;
        break;
      case 'oneOf':
        result = (propType.args as unknown[]).map((a) => `"${a}"`).join(' | ');
        break;
      case 'oneOfType':
        result = `${propTypeFormat(propType.args as ReactDescT[], ' | ')}`;
        break;
      case 'shape':
        result = `${shapeFormat(propType.args as Record<string, ReactDescT>)}`;
        break;
      default:
        result = `${propType.type}`;
        break;
    }
  } else {
    result = 'any';
  }
  return result;
};

const propTypeAsTypescript = (propType: ReactDescT, propName: string) => {
  const documentation = {
    ...propType.reactDesc,
    name: propName,
  };

  documentation.format = propTypeFormat(propType);

  return documentation;
};

export default function descToTypescript<C, R>(
  component: React.ComponentType<C> | Hook<C, R>,
  reactDesc: ComponentDocumentation,
) {
  if (!component) {
    throw new Error('react-desc: component is required');
  }

  const documentation: Partial<TypescriptDocumentation> = {
    name: component.displayName || component.name,
    ...reactDesc,
  };
  if (reactDesc) {
    delete documentation.propTypes;

    if (reactDesc.propTypes) {
      const propTypes: TypescriptDocumentation['properties'] = [];
      Object.keys(reactDesc.propTypes).forEach((propName) => {
        const propType = reactDesc.propTypes[propName];
        if (propType.type === 'shape') {
          propType.reactDesc.group = {};

          Object.keys(propType.args as Record<string, ReactDescT>).forEach((prop) => {
            if (!propType.reactDesc.group) return;
            propType.reactDesc.group[prop] = propTypeAsTypescript(
              (propType.args as Record<string, ReactDescT>)[prop],
              prop,
            );
          });
        }
        propTypes.push(propTypeAsTypescript(propType, propName));
      });
      if (propTypes.length > 0) {
        documentation.properties = propTypes;
      }
    }
  }
  return documentation as TypescriptDocumentation;
}
